import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/i18n/LocaleProvider";
import { fetchTranslations, applyTranslation } from "@/lib/blogI18n";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Loader2, ArrowRight, Search, X, Sparkles } from "lucide-react";
import { imageSrcSet } from "@/lib/media";

type PostCard = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_url: string;
  tags: string[];
  published_at: string | null;
};

const normalize = (s: string) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss");

const Blog = () => {
  const { t } = useTranslation();
  const { lang, localize } = useLocale();
  const [query, setQuery] = useState("");
  const [aiIds, setAiIds] = useState<string[] | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["blog-posts", lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, content, cover_url, tags, published_at")
        .eq("status", "published")
        .order("published_at", { ascending: false });
      if (error) throw error;
      const posts = (data as PostCard[]) ?? [];
      const translations = await fetchTranslations(posts.map((p) => p.id), lang);
      return posts.map((p) => applyTranslation(p, translations.get(p.id)));
    },
  });

  const terms = useMemo(
    () => normalize(query).split(/\s+/).filter((w) => w.length > 1),
    [query],
  );

  const keywordResults = useMemo(() => {
    if (!data) return [];
    if (!terms.length) return data;
    return data.filter((p) => {
      const haystack = normalize(
        `${p.title} ${p.excerpt} ${p.content} ${(p.tags ?? []).join(" ")}`,
      );
      return terms.every((w) => haystack.includes(w));
    });
  }, [data, terms]);

  const results = useMemo(() => {
    if (!data) return [];
    if (aiIds) {
      const order = new Map(aiIds.map((id, i) => [id, i]));
      return data
        .filter((p) => order.has(p.id))
        .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }
    return keywordResults;
  }, [data, aiIds, keywordResults]);

  const runAiSearch = async () => {
    if (query.trim().length < 2) return;
    setAiLoading(true);
    setAiError(false);
    setAiIds(null);
    try {
      const { data: res, error } = await supabase.functions.invoke("blog-search", {
        body: { query: query.trim(), lang },
      });
      if (error) throw error;
      setAiIds((res as { ids?: string[] })?.ids ?? []);
    } catch {
      setAiError(true);
    } finally {
      setAiLoading(false);
    }
  };

  const resetSearch = () => {
    setQuery("");
    setAiIds(null);
    setAiError(false);
  };

  // Blog hub entity graph: the Blog itself, an ItemList of the published posts
  // (so AI search surfaces can resolve the hub -> article relationships without
  // executing JS on every detail page) and a breadcrumb trail.
  const blogUrl = `https://devcore.systems${localize("/blog")}`;
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Blog",
          "@id": `${blogUrl}#blog`,
          url: blogUrl,
          name: `${t("blogPage.title")} ${t("blogPage.titleAccent")}`.trim(),
          description: t("blogPage.metaDescription"),
          inLanguage: lang,
          publisher: { "@type": "Organization", "@id": "https://devcore.systems/#organization", name: "DevCore" },
          blogPost: (data ?? []).slice(0, 20).map((p) => ({
            "@type": "BlogPosting",
            "@id": `https://devcore.systems${localize(`/blog/${p.slug}`)}`,
            headline: p.title,
            url: `https://devcore.systems${localize(`/blog/${p.slug}`)}`,
            datePublished: p.published_at || undefined,
          })),
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${blogUrl}#breadcrumb`,
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: t("blogPage.home"),
              item: `https://devcore.systems${localize("/")}`,
            },
            { "@type": "ListItem", position: 2, name: t("nav.blog"), item: blogUrl },
          ],
        },
      ],
    }),
    [data, blogUrl, lang, localize, t],
  );

  return (
    <div className="min-h-screen">
      <SEOHead
        title={t("blogPage.metaTitle")}
        description={t("blogPage.metaDescription")}
        canonical="https://devcore.systems/blog"
        jsonLd={jsonLd}
      />

      <Navbar />

      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-[0.1] pointer-events-none" />
        <div className="absolute -left-20 top-1/4 w-96 h-96 bg-primary/[0.07] blur-[120px] rounded-full pointer-events-none" />
        <div className="container relative z-10">
          <ScrollReveal>
            <p className="mono-label mb-4">// {t("blogPage.label")}</p>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-[1.05] tracking-tight max-w-3xl">
              {t("blogPage.title")} <span className="gradient-text">{t("blogPage.titleAccent")}</span>
            </h1>
            <p className="mt-6 text-muted-foreground text-lg max-w-xl">
              {t("blogPage.subtitle")}
            </p>
          </ScrollReveal>

          <ScrollReveal delay={80}>
            <div className="mt-10 max-w-2xl">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setAiIds(null);
                      setAiError(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") runAiSearch();
                    }}
                    placeholder={t("blogPage.searchPlaceholder")}
                    aria-label={t("blogPage.searchPlaceholder")}
                    className="w-full h-12 rounded-xl border border-border bg-card/50 backdrop-blur-xl pl-11 pr-11 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent/60 transition-colors [&::-webkit-search-cancel-button]:hidden"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={resetSearch}
                      aria-label={t("blogPage.searchClear")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={runAiSearch}
                  disabled={query.trim().length < 2 || aiLoading}
                  className="h-12 shrink-0 inline-flex items-center justify-center gap-2 rounded-xl border border-accent/40 bg-accent/10 px-5 text-sm font-medium text-accent hover:bg-accent/20 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  {aiLoading ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Sparkles size={15} />
                  )}
                  {t("blogPage.searchAi")}
                </button>
              </div>

              {(query || aiError) && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  {query && !aiLoading && (
                    <span className="mono-label">
                      {aiIds ? t("blogPage.searchAiActive") : ""}{" "}
                      {results.length} {t("blogPage.searchResults")}
                    </span>
                  )}
                  {aiError && (
                    <span className="text-xs text-destructive">{t("blogPage.searchError")}</span>
                  )}
                </div>
              )}
            </div>

          </ScrollReveal>

          <div className="mt-14">

            {isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="animate-spin text-accent" />
              </div>
            ) : !data?.length ? (
              <p className="text-muted-foreground">{t("blogPage.empty")}</p>
            ) : !results.length ? (
              <div className="py-6">
                <p className="text-muted-foreground">{t("blogPage.searchEmpty")}</p>
                {!aiIds && (
                  <button
                    type="button"
                    onClick={runAiSearch}
                    className="mt-3 inline-flex items-center gap-2 text-sm text-accent hover:underline"
                  >
                    <Sparkles size={14} /> {t("blogPage.searchAi")}
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((p, i) => (

                  <ScrollReveal key={p.id} delay={i * 80} className="min-w-0">

                    <Link
                      to={localize(`/blog/${p.slug}`)}
                      className="group block rounded-2xl border border-border bg-card/50 backdrop-blur-xl overflow-hidden hover:border-accent/50 transition-colors h-full"
                    >
                      {p.cover_url ? (
                        <img
                          {...imageSrcSet(p.cover_url, 450, 74)}
                          alt={p.title}
                          loading="lazy"
                          decoding="async"
                          width={450}
                          height={253}
                          sizes="(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 92vw"
                          className="w-full aspect-[16/9] object-cover"
                        />
                      ) : (
                        <div className="w-full aspect-[16/9] bg-gradient-to-br from-primary/20 to-accent/10 tech-grid" />
                      )}
                      <div className="p-6">
                        {p.tags?.[0] && (
                          <span className="mono-label">/{p.tags[0]}</span>
                        )}
                        <h2 className="text-xl font-bold text-foreground mt-2 group-hover:text-accent transition-colors">
                          {p.title}
                        </h2>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {p.excerpt}
                        </p>
                        <span className="inline-flex items-center gap-1 text-accent text-sm mt-4 font-medium">
                          {t("blogPage.read")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
